import type { ContractExportStatus, ContractExportView } from "@closer/shared";
import { Badge, Button, Card, Checkbox, EmptyState, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { formatDateTime } from "../../lib/format";
import { usePreferences } from "../../lib/preferences";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * SÖZLEŞME PAKETİNİ GÜVENLİ DIŞA AKTARMA (CLOS-234 `[198]`, alt görev CLOS-1040).
 *
 * ### ⚠ Durumlar SUNUCUDAN — yarım paket asla "hazır" gösterilmiyor
 *
 * Kart: *"durumlar `Hazırlanıyor → Hazır → Başarısız / İptal edildi / Süresi doldu`
 * olarak server state'ine göre gösterilir."* Bu bileşen kendi ilerleme yüzdesini
 * uydurmuyor; yalnız sunucunun döndürdüğü `status` alanını çiziyor.
 *
 * ### ⚠ İndirme adresi TIKLANDIĞI AN isteniyor
 *
 * `ContractAttachments`'taki `indir` mutasyonuyla AYNI desen (`[101]` kuralı): imzalı
 * adres DOM'da durmuyor, her tıklamada erişim yeniden doğrulanıyor.
 */

const STATUS_LABELS: Record<ContractExportStatus, string> = {
  pending: "Hazırlanıyor",
  processing: "Hazırlanıyor",
  ready: "Hazır",
  failed: "Başarısız",
  cancelled: "İptal edildi",
  expired: "Süresi doldu",
};

const STATUS_TONE: Record<ContractExportStatus, "success" | "warning" | "danger" | "neutral"> = {
  pending: "warning",
  processing: "warning",
  ready: "success",
  failed: "danger",
  cancelled: "neutral",
  expired: "neutral",
};

export function ContractExportSection({
  contractId,
  kayitYazma = true,
}: {
  contractId: string;
  kayitYazma?: boolean;
}) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const { preferences } = usePreferences();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [includeMainDocument, setIncludeMainDocument] = useState(true);
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [includeComments, setIncludeComments] = useState(false);
  const [includeActivitySummary, setIncludeActivitySummary] = useState(false);

  /** ⚠ `contract.export` AYRI yetki (kart kriteri) — `contract.edit` yeterli DEĞİL. */
  const disaAktarabilir = can("contract.export") && kayitYazma;
  const hicbiriSecilmedi =
    !includeMainDocument && !includeMetadata && !includeComments && !includeActivitySummary;

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "contract", contractId, "disa-aktarmalar"),
    queryFn: async () =>
      (await api<ContractExportView[]>(`/api/contracts/${contractId}/exports`, { tenantId })).data,
    // Hazırlanan işler için kısa aralıklı yoklama — bildirim değil, sorgulama (ADR-0037).
    refetchInterval: (q) =>
      (q.state.data ?? []).some((e) => e.status === "pending" || e.status === "processing")
        ? 3000
        : false,
  });

  const yenile = () =>
    queryClient.invalidateQueries({
      queryKey: tenantKey(tenantId, "contract", contractId, "disa-aktarmalar"),
    });

  const talepEt = useMutation({
    mutationFn: () =>
      api<ContractExportView>(`/api/contracts/${contractId}/exports`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({
          includeMainDocument,
          includeMetadata,
          includeComments,
          includeActivitySummary,
        }),
      }),
    onSuccess: async () => {
      await yenile();
      toast.success("Paket hazırlanıyor");
    },
    onError: (hata) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Dışa aktarma talep edilemedi"),
  });

  const iptalEt = useMutation({
    mutationFn: (exportId: string) =>
      api(`/api/contracts/${contractId}/exports/${exportId}/cancel`, { method: "POST", tenantId }),
    onSuccess: yenile,
    onError: (hata) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "İptal edilemedi"),
  });

  const yenidenDene = useMutation({
    mutationFn: (exportId: string) =>
      api(`/api/contracts/${contractId}/exports/${exportId}/retry`, { method: "POST", tenantId }),
    onSuccess: yenile,
    onError: (hata) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Yeniden denenemedi"),
  });

  const indir = useMutation({
    mutationFn: async (exportId: string) =>
      (
        await api<{ downloadUrl: string }>(
          `/api/contracts/${contractId}/exports/${exportId}/download`,
          {
            tenantId,
          },
        )
      ).data,
    onSuccess: (bilet) => {
      const bag = document.createElement("a");
      bag.href = bilet.downloadUrl;
      bag.rel = "noopener noreferrer";
      document.body.appendChild(bag);
      bag.click();
      bag.remove();
    },
    onError: (hata) =>
      toast.error(
        hata instanceof ApiRequestError
          ? hata.message
          : "Paket indirilemedi; süresi dolmuş veya erişiminiz kalkmış olabilir",
      ),
  });

  if (!can("contract.export")) return null;

  return (
    <Card title="Sözleşme paketini dışa aktar">
      {sorgu.isPending ? (
        <div role="status" aria-label="Dışa aktarmalar yükleniyor">
          <Skeleton lines={2} />
        </div>
      ) : sorgu.error || !sorgu.data ? (
        <StateView
          state={resolveErrorState(sorgu.error, { idempotent: true })}
          onRetry={() => void sorgu.refetch()}
        />
      ) : sorgu.data.length === 0 ? (
        <EmptyState
          title="Dışa aktarma yok"
          description="Bu sözleşme için henüz bir paket talep edilmedi."
        />
      ) : (
        <ul className="flex flex-col divide-y divide-border-default">
          {sorgu.data.map((e) => (
            <li key={e.id} className="flex flex-wrap items-center gap-2 py-3 text-sm">
              <Badge tone={STATUS_TONE[e.status]}>{STATUS_LABELS[e.status]}</Badge>
              <span className="text-fg-soft">
                {formatDateTime(e.requestedAt, preferences.locale)}
              </span>
              {e.manifest && (
                <span className="text-xs text-fg-faint">
                  {e.manifest.includedClasses.length} içerik sınıfı · {e.manifest.entries.length}{" "}
                  dosya
                </span>
              )}
              <span className="ml-auto flex gap-2">
                {e.status === "ready" && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    loading={indir.isPending}
                    onClick={() => indir.mutate(e.id)}
                  >
                    İndir
                  </Button>
                )}
                {(e.status === "failed" || e.status === "expired" || e.status === "cancelled") &&
                  disaAktarabilir && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      loading={yenidenDene.isPending}
                      onClick={() => yenidenDene.mutate(e.id)}
                    >
                      Yeniden dene
                    </Button>
                  )}
                {e.status === "pending" && disaAktarabilir && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    loading={iptalEt.isPending}
                    onClick={() => iptalEt.mutate(e.id)}
                  >
                    İptal et
                  </Button>
                )}
              </span>
              {e.status === "failed" && e.failureReason && (
                <p role="alert" className="w-full text-xs text-danger">
                  {e.failureReason}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {disaAktarabilir && (
        <div className="mt-4 flex flex-col gap-3 border-t border-border-default pt-4">
          <div className="flex flex-col gap-2">
            <Checkbox
              label="Ana belge"
              checked={includeMainDocument}
              onChange={(e) => setIncludeMainDocument(e.target.checked)}
            />
            <Checkbox
              label="Sözleşme bilgileri (metadata)"
              checked={includeMetadata}
              onChange={(e) => setIncludeMetadata(e.target.checked)}
            />
            <Checkbox
              label="Paylaşılan yorumlar"
              checked={includeComments}
              onChange={(e) => setIncludeComments(e.target.checked)}
            />
            <Checkbox
              label="Faaliyet özeti"
              checked={includeActivitySummary}
              onChange={(e) => setIncludeActivitySummary(e.target.checked)}
            />
          </div>
          {/*
            ⚠ İç özel yorumlar ve organizasyona özel katmanlar BU LİSTEDE YOK: kart
            "yalnızca izinli paylaşılan içerik seçilebilir" diyor — seçim listesi zaten
            gizli sınıfı hiç sunmuyor.
          */}
          <div>
            <Button
              type="button"
              loading={talepEt.isPending}
              disabled={hicbiriSecilmedi}
              onClick={() => talepEt.mutate()}
            >
              Paket hazırla
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
